// overview-tab-content.tsx
import React, { useEffect, useState } from "react";
import "../../../../newComponents/charts/expense-distribution/expense-distribution.css";
import ExpenseDistribution from "../../../charts/expense-distribution/expense-distribution";
import ProfitLossSummary from "../../../charts/profit-loss-summary/profit-loss-summary";
import LastTransactions from "../../../charts/last-transactions/last-transactions";
import { OrganizationService, AnalyticsService, DateFilterTypesEnum, UserDashboardService, EntityType, FilterEntityEnum } from "@propertelligent/client-api";
import { useParams } from "react-router-dom";
import SimpleSpinner from "../../../../components/loader";
import apiHelper, { ApiNotificationSettings } from "../../../../utils/apiHelper";
import { UserLoginData } from "../../../../components/common/charts/Enums";
import DateHelper from "../../../../utils/DateHelper";
import DonutChart from "../../../charts/revenue-overview/revenue-overview";
// Import the Modal
import AddPaymentModal from "../../../payments/AddPaymentModal";

interface OverviewTabContentProps {
  result: any[];
  entityTypeId?: EntityType;
  onPaymentAction?: () => void;
}

interface ExpenseCategory {
  name: string;
  amount: number;
  color: string;
  formattedAmount?: string;
  formatted?: {
    Amount: string;
    AmountRaw: string;
    ExpenseType?: string;
  };
}

interface ProfitLossData {
  month: string
  profit: number
  loss: number
}


interface RevenueOverviewData {
  netProfit: number;
  netProfitFormatted: string;
  expense: number;
  expenseFormatted: string;
  totalRevenue: number;
  totalRevenueFormatted: string;
  totalRevenueRaw: string;
  expenseRaw: string;
  netProfitRaw: string;
  chartData?: { name: string; value: number; color: string }[];
}

const OverviewTabContent: React.FC<OverviewTabContentProps> = ({ result, entityTypeId, onPaymentAction }) => {
  const { id } = useParams();
  const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id));
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);
  const [revenueData, setRevenueData] = useState<RevenueOverviewData | null>(null);
  const [expenseLoading, setExpenseLoading] = useState(true);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [pnlLoading, setPnlLoading] = useState(true);
  const [pnlChartData, setPnlChartData] = useState<ProfitLossData[]>([]);

  const [cat_monthValue, setCat_MonthValue] = useState<any>("Last 12 Month");
  const [cat_revenueMonthValue, setCat_revenueMonthValue] = useState<any>("Last 12 Month");

  const [selectedRangePNL, setSelectedRangePNL] = useState<DateFilterTypesEnum>(DateFilterTypesEnum.Last12Months);
  const [selectedRangeRev, setSelectedRangeRev] = useState<DateFilterTypesEnum>(DateFilterTypesEnum.Last12Months);

  // Add state variables
  const [transactionPropertyList, setTransactionPropertyList] = useState<any[]>([]);
  const [transactionPropertyLoading, setTransactionPropertyLoading] = useState(true);
  const organizationId = parseInt(sessionStorage.getItem(UserLoginData.organization_id));

  // Modal State
  const [paymentModalConfig, setPaymentModalConfig] = useState({
    isOpen: false,
    entityId: null as number | null,
    entityType: EntityType.Property
  });

  const DateRangeToEnumMap: { [key: string]: number } = {
    "Last 12 Month": DateFilterTypesEnum.Last12Months,
    "Last 6 Month": DateFilterTypesEnum.Last6Months,
    "Last 3 Month": DateFilterTypesEnum.Last3Months,
    "This Month": DateFilterTypesEnum.MonthToDate,
  };

  const getExpenseStats = () => {
    setExpenseLoading(true);

    // Calculate the Enum value from the string state
    const filterEnum = cat_monthValue && DateRangeToEnumMap[cat_monthValue]
      ? DateRangeToEnumMap[cat_monthValue]
      : DateFilterTypesEnum.Last12Months;

    apiHelper(
      entityTypeId == EntityType.Property ? AnalyticsService.getApiAnalyticsPropertyExpensebycategory
        : entityTypeId == EntityType.Unit ? AnalyticsService.getApiAnalyticsUnitExpensebycategory
          : AnalyticsService.getApiAnalyticsPropertyExpensebycategory,
      { showNotification: false },
      Number(id),           // propertyId/unitId
      undefined,            // pageSize
      undefined,            // pageNumber
      undefined,            // skipValue
      undefined,            // filterEntityTypeId
      undefined,            // filterEntityIds
      undefined,            // fullyQualifiedFilters (REMOVED String Filter)
      undefined,            // fullyQualifiedSorts
      filterEnum,           // dateFilterDateFilterTypeId (Arg 9)
      new Date().toISOString() // dateFilterClientDate (Arg 10)
    )
      .then((res) => {
        const rawItems = res?.data?.success;

        if (rawItems) {
          const categories = rawItems.map((item: any, index: number) => ({
            name: item.formatted.ExpenseType || "Unknown",
            amount: parseFloat(item.formatted.AmountRaw.replace(/,/g, "")) || 0,
            formattedAmount: item.formatted.Amount,
            formatted: item.formatted,
            color: ["#2563eb", "#f97316", "#059669", "#db2777", "#c026d3"][index % 5],
          }));
          setExpenseCategories(categories);
        }
        else {
          setExpenseCategories([]);
        }
        setExpenseLoading(false);
      })
      .catch((err) => {
        console.error("Service Request API Error:", err);
        setExpenseCategories([]);
        setExpenseLoading(false);
      });
  };

  const getRevenueStats = (DateFilterTypesEnum?: number) => {
    setRevenueLoading(true);

    const filterEnum = DateFilterTypesEnum || DateHelper.getDateFilterType("Last 12 Month");

    apiHelper(
      entityTypeId == EntityType.Property ? AnalyticsService.getApiCashFlowPropertyCashflow
        : entityTypeId == EntityType.Unit ? AnalyticsService.getApiCashFlowUnitCashflow
          : AnalyticsService.getApiCashFlowPropertyCashflow,
      { showNotification: false },
      Number(id),           // propertyId/unitId
      undefined,            // pageSize
      undefined,            // pageNumber
      undefined,            // skipValue
      undefined,            // filterEntityTypeId
      undefined,            // filterEntityIds
      undefined,            // fullyQualifiedFilters (REMOVED String Filter)
      undefined,            // fullyQualifiedSorts
      filterEnum,           // dateFilterDateFilterTypeId (Arg 9)
      new Date().toISOString() // dateFilterClientDate (Arg 10)
    )
      .then((res) => {
        const successData = res?.data?.success;

        if (successData) {
          const { totalIncome, totalExpense, netIncome, formatted } = successData;

          const result: RevenueOverviewData = {
            totalRevenue: totalIncome || 0,
            totalRevenueFormatted: formatted?.TotalIncome || "0",
            totalRevenueRaw: formatted?.TotalIncomeRaw || "0",
            expense: totalExpense || 0,
            expenseFormatted: formatted?.TotalExpense || "0",
            expenseRaw: formatted?.TotalExpenseRaw || "0",
            netProfit: netIncome || 0,
            netProfitFormatted: formatted?.NetIncome || "0",
            netProfitRaw: formatted?.NetIncomeRaw || "0",
          };

          setRevenueData({ ...result });
        } else {
          setRevenueData(null);
        }
        setRevenueLoading(false);
      })
      .catch((err) => {
        console.error("Revenue Overview API Error:", err);
        setRevenueData(null);
        setRevenueLoading(false);
      });
  };

  const getPnlReport = (DateFilterTypesEnum?: number) => {
    setPnlLoading(true);

    const filterEnum = DateFilterTypesEnum || DateHelper.getDateFilterType("Last 12 Month");

    apiHelper(
      entityTypeId == EntityType.Property
        ? AnalyticsService.getApiCashFlowPropertyCashflow
        : entityTypeId == EntityType.Unit
          ? AnalyticsService.getApiCashFlowUnitCashflow
          : AnalyticsService.getApiCashFlowPropertyCashflow,
      { showNotification: false },
      Number(id),           // propertyId/unitId
      undefined,            // pageSize
      undefined,            // pageNumber
      undefined,            // skipValue
      undefined,            // filterEntityTypeId
      undefined,            // filterEntityIds
      undefined,            // fullyQualifiedFilters (REMOVED String Filter)
      undefined,            // fullyQualifiedSorts
      filterEnum,           // dateFilterDateFilterTypeId (Arg 9)
      new Date().toISOString() // dateFilterClientDate (Arg 10)
    )
      .then((res) => {
        const pnlData = res?.data?.success?.cashFlowItems;

        if (Array.isArray(pnlData)) {
          const formatted = pnlData.map((item: any) => ({
            month: `${item.month}/${item.year}`,
            profit: Number(item.income) || 0,
            loss: item.expense ? -Math.abs(Number(item.expense)) : 0,
          }));

          setPnlChartData(formatted);
        } else {
          setPnlChartData([]);
        }
        setPnlLoading(false);
      })
      .catch((err) => {
        console.error("PNL Report API Error:", err);
        setPnlChartData([]);
        setPnlLoading(false);
      });
  };

  useEffect(() => {
    if (id && entityTypeId !== undefined) {
      getPnlReport(selectedRangePNL);
    }
  }, [id, entityTypeId]);


  useEffect(() => {
    getExpenseStats();
  }, [cat_monthValue]);

  const handleDateRangeChange_PNL = (newRange: string) => {
    const enumValue = DateRangeToEnumMap[newRange];
    if (enumValue) {
      setSelectedRangePNL(enumValue);

      getPnlReport(enumValue);
    }
  };

  const handleTimeRangeChange_Rev = (newRange: string) => {
    const enumValue = DateRangeToEnumMap[newRange];
    if (enumValue) {
      setSelectedRangeRev(enumValue);
      setCat_revenueMonthValue(newRange);
    }
  };


  useEffect(() => {
    if (cat_revenueMonthValue) {
      const enumValue = DateRangeToEnumMap[cat_revenueMonthValue];
      getRevenueStats(enumValue);
    }
  }, [cat_revenueMonthValue]);

  const getTransactionPropertyList = () => {
    setTransactionPropertyLoading(true);


    apiHelper(
      entityTypeId === EntityType.Property
        ? OrganizationService.getApiOrganizationTransactionPropertylistInclUnits
        : entityTypeId === EntityType.Unit ? OrganizationService.getApiOrganizationTransactionOrganizationlist
          : OrganizationService.getApiOrganizationTransactionPropertylistInclUnits,
      { showNotification: false },
      entityTypeId == EntityType.Property ? Number(id) : entityTypeId == EntityType.Unit ? orgId : undefined, // organizationId or propertyId
      5,          // pageSize
      1,          // pageNumber
      undefined,  // skipValue
      entityTypeId == EntityType.Property ? undefined : entityTypeId == EntityType.Unit ? 4 : undefined,  // filterEntityTypeId
      entityTypeId == EntityType.Property ? undefined : entityTypeId == EntityType.Unit ? Number(id) : undefined,  // filterEntityIds
      null,       // fullyQualifiedFilters
      entityTypeId == EntityType.Property ? "PaymentDate desc" : entityTypeId == EntityType.Unit ? undefined : "PaymentDate desc" // fullyQualifiedSorts
    )
      .then((res) => {
        const rawItems = res?.data?.success;
        if (Array.isArray(rawItems)) {
          setTransactionPropertyList(rawItems);
        } else {
          setTransactionPropertyList([]);
        }
        setTransactionPropertyLoading(false);
      })
      .catch((err) => {
        console.error("API Error:", err);
        setTransactionPropertyList([]);
        setTransactionPropertyLoading(false);
      });
  };

  // Call the API when component mounts or when any dependent value changes
  useEffect(() => {
    getTransactionPropertyList();
  }, [id, entityTypeId]);

  const formattedTransactions = transactionPropertyList.map((transaction: any) => ({
    id: transaction.id.toString(),
    type: transaction.description || "Rent",
    person: transaction.payeeAccountName || "Unknown",
    unit: transaction.unitCode || "N/A",
    amount: transaction.formatted.Amount || 0,
    amountRaw: transaction.formatted.AmountRaw,
    date: transaction.formatted.PaymentDateDateOnly
  }));

  return (
    <>
      {transactionPropertyLoading ? (
        <SimpleSpinner myClassName={"Icon50 mt-5"} isFullPage={true} />
      ) : (
        <>
          <div className="top-listing-header-top" style={{ paddingTop: "24px" }}>
            <h2 className="H2">Overview</h2>
          </div>
          <div className="row">
            <div className="col-6">
              <ExpenseDistribution
                categories={expenseCategories}
                heading={"Expense Distribution"}
                setCat_MonthValue={setCat_MonthValue}
                defaultTimeRange={cat_monthValue}
                isLoading={expenseLoading}
              />
            </div>
            <div className="col-6">
              <DonutChart
                totalName="Total Revenue"
                heading="Revenue Overview"
                data={[
                  {
                    title: "Net Profit",
                    value: revenueData?.netProfit || 0,
                    formattedValue: revenueData?.netProfitFormatted,
                    valueRaw: revenueData?.netProfitRaw,
                    color: "#1e40af"
                  },
                  {
                    title: "Expense",
                    value: revenueData?.expense || 0,
                    formattedValue: revenueData?.expenseFormatted,
                    valueRaw: revenueData?.expenseRaw,
                    color: "#ff5722"
                  },
                ]}
                valueTotal={revenueData?.totalRevenueFormatted || "0"}
                valueTotalRaw={revenueData?.totalRevenueRaw}
                symbolPosition="before"
                isAlertStrip={false}
                onTimeRangeChange={handleTimeRangeChange_Rev}
                isLoading={revenueLoading}
              />
            </div>
            <div className="col-6">
              <LastTransactions
                transactions={formattedTransactions}
                entityId={Number(id)}
                isLoading={transactionPropertyLoading}
                // Pass handler to open modal
                onAddPayment={() => setPaymentModalConfig({
                  isOpen: true,
                  entityId: Number(id),
                  entityType: entityTypeId || EntityType.Property
                })}
              />
            </div>
            <div className="col-6">
              <ProfitLossSummary isLoading={pnlLoading} data={pnlChartData} onTimeRangeChange={handleDateRangeChange_PNL} />
            </div>
          </div>
        </>
      )}

      {/* Add Payment Modal */}
      {paymentModalConfig.isOpen && (
        <AddPaymentModal
          isOpen={paymentModalConfig.isOpen}
          onClose={() => {
            setPaymentModalConfig(prev => ({ ...prev, isOpen: false }));
            getTransactionPropertyList(); // Refresh transactions locally

            if (onPaymentAction) {
              onPaymentAction();
            }
          }}
          entityIdProp={paymentModalConfig.entityId || undefined}
          entityTypeIdProp={paymentModalConfig.entityType}
          defaultPaymentMode="1"
        />
      )}
    </>
  );
};

export default OverviewTabContent;